import React, { useState, useEffect } from "react";
import axios from "axios";
import UpdateModal from "./UpdateModal";
import FinishGoodModal from "./modals/FinishGoodModal";
import { TbTrashX, TbChecks } from "react-icons/tb";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingOverlay from "./LoadingOverlay";
import { toast } from "react-toastify";

const TABS = [
  { label: "Jobsheet", key: "JOBSHEET", color: "blue" },
  { label: "On Process", key: "ON PROCESS", color: "green" },
  { label: "QC Check", key: "QC CHECK", color: "yellow" },
  { label: "Rework", key: "REWORK", color: "red" },
];

function tabStyle(color, active) {
  const base =
    "px-5 py-2 text-sm font-semibold rounded-t-lg transition focus:outline-none whitespace-nowrap";
  if (!active)
    return `${base} text-gray-500 hover:text-gray-700 hover:bg-gray-100`;
  const map = {
    blue: "bg-blue-600 text-white",
    green: "bg-green-600 text-white",
    yellow: "bg-yellow-500 text-white",
    red: "bg-red-500 text-white",
  };
  return `${base} ${map[color]}`;
}

function statusBadge(status) {
  switch (status) {
    case "JOBSHEET":
      return "bg-blue-500 text-white";
    case "ON PROCESS":
      return "bg-green-500 text-white";
    case "QC CHECK":
      return "bg-yellow-500 text-white";
    case "FINISHED GOOD":
      return "bg-gray-500 text-white";
    case "REWORK":
      return "bg-red-500 text-white";
    default:
      return "bg-gray-300 text-gray-700";
  }
}

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showFGModal, setShowFGModal] = useState(false);
  const [orderData, setOrderData] = useState();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getOrders();
  }, []);

  const getOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        "https://eps.elkomprintama.com/get/api/orders",
      );
      setOrders(response.data.result);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteOrder = async (productId) => {
    setIsLoading(true);
    try {
      await axios.delete(
        `https://eps.elkomprintama.com/delete/api/orders/${productId}`,
      );
      toast.success("Deleted");
      getOrders();
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Error deleting order");
    } finally {
      setIsLoading(false);
    }
  };

  const currentKey = TABS[activeTab].key;
  const filtered = orders.filter((o) => o.job_status === currentKey);

  return (
    <>
      <div className="w-full rounded-lg bg-green-600 mb-4">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Master Order
        </label>
      </div>

      <div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col relative"
        style={{ maxHeight: "calc(100vh - 160px)" }}
      >
        {isLoading && <LoadingOverlay message="Loading" />}

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-4 pt-3 gap-1 bg-gray-50 flex-shrink-0 overflow-x-auto">
          {TABS.map((tab, i) => {
            const count = orders.filter((o) => o.job_status === tab.key).length;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(i)}
                className={tabStyle(tab.color, activeTab === i)}
              >
                {tab.label}
                <span
                  className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${
                    activeTab === i
                      ? "bg-white/30"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          {filtered.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm italic">
              No orders in this stage.
            </div>
          ) : (
            <table className="text-sm w-full text-left text-gray-600 border-collapse">
              <thead className="sticky top-0 z-10 text-xs text-white uppercase bg-gray-600">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 border-r text-center">
                    Status
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">
                    Customer
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">
                    Job Name
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">Size</th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">Qty</th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">
                    PO Number
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">
                    Delivery Date
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">
                    Operator
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 border-r">QC</th>
                  <th className="whitespace-nowrap px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order, index) => (
                  <tr
                    key={index}
                    className={`border-b hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                    onDoubleClick={() => {
                      setOrderData(order);
                      setShowModal(true);
                    }}
                  >
                    <td className="px-4 py-2 text-center border-r">
                      <span
                        className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${statusBadge(order.job_status)}`}
                      >
                        {order.job_status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r">
                      {order.nme}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r">
                      {order.job_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r text-center">
                      {order.size}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r text-center">
                      {order.qty}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r">
                      {order.po_num}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r text-center">
                      {new Date(order.dlvr_date).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r">
                      {order.opr_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 border-r">
                      {order.qc_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                          onClick={(e) => {
                            e.preventDefault();
                            setOrderData(order);
                            setShowConfirm(true);
                          }}
                        >
                          <TbTrashX size="1.3em" />
                        </button>
                        <button
                          className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50"
                          onClick={() => {
                            setOrderData(order);
                            setShowFGModal(true);
                          }}
                        >
                          <TbChecks size="1.3em" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <UpdateModal
        isVisible={showModal}
        orderData={orderData}
        onClose={() => setShowModal(false)}
        refreshData={getOrders}
      />
      <FinishGoodModal
        isVisible={showFGModal}
        orderData={orderData}
        onClose={() => setShowFGModal(false)}
        refreshOrders={getOrders}
      />
      {showConfirm && (
        <ConfirmDialog
          message={`Are you sure you want to delete Order ID: ${orderData?.order_id}?`}
          onConfirm={() => {
            setShowConfirm(false);
            deleteOrder(orderData.order_id);
          }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
};

export default OrderList;
